/**
 * DOCTOR-ONE-CLOSE-AND-TRUST-V1 -- the ONE-CLOSE act.
 *
 * The founder verdict (2026-07-09): the visit ended THREE times -- per-procedure
 * accepted/declined, then "Finalize visit" outcome, then "Checkout"
 * converted/not-converted + reason -- the same truth asked thrice in three
 * vocabularies, contradictions possible by construction. This module collapses
 * them into ONE act driven by a DERIVATION MATRIX:
 *
 *   plan status (per procedure: advised / accepted / declined) -- marked INLINE
 *     |
 *     v   deriveVisitClose (PURE)
 *   visit outcome (treatment_advised / accepted / declined -- EXISTING vocab)
 *   + conversion (lead_converted / lead_not_converted)
 *
 * Contradiction-impossible BY CONSTRUCTION: the outcome AND the conversion are
 * BOTH derived from the ONE plan snapshot, so accepted ALWAYS pairs with
 * converted and declined ALWAYS pairs with not-converted. The doctor cannot
 * record accepted-but-not-converted the way the old two-picker surface allowed.
 *
 * ORCHESTRATION (run record 1(c)): closeVisit is a CLIENT-SIDE orchestrator
 * (mirroring the existing finalizeVisit precedent) composing the EXISTING
 * audited domain writes -- NO new endpoint, NO new write semantics, NO schema.
 * Each derived fact flows through its own endpoint, which stamps the actor
 * principal + role on an appointment_events row (the audit trail is intact).
 * It is IDEMPOTENT (skips a step the server already reflects) so a retry after
 * a partial failure completes the rest without duplicating an event.
 *
 * DERIVED-AUDIT: "derived-from" is provable end to end -- the plan-item statuses
 * are the audited SOURCE, deriveVisitClose is a PURE unit-tested function, and
 * the outcome events are the audited RESULT. Pure parts are React-free and
 * unit-tested by direct call (repo idiom, no jsdom).
 */

import type { ClinicalPlanItemView } from '@/dashboard/api';
import type {
  OutcomeAction,
  WorkbenchFeedback,
} from '@/dashboard/clinical-workbench/clinical-workbench-view';
import {
  primaryRecoverableDecline,
  rollupPlan,
} from '@/dashboard/clinical-workbench/case-sheet-cockpit-view';

// --- The DERIVATION MATRIX (pure) --------------------------------------------

/** The conversion leg the close will record (or none for an advised-only close). */
export type CloseConversion = 'converted' | 'not_converted' | 'none';

/** The doctor's answer to the inline question when the plan marks NOTHING
 *  (R-C). null = not answered yet -> the close is blocked on the question. */
export type CloseExplicitPick = 'advised' | 'accepted' | 'declined' | null;

export interface VisitCloseDerivation {
  /** The visit outcome to record (EXISTING vocabulary), or null when nothing
   *  is derivable yet (the un-answered inline question). */
  readonly visitOutcome: OutcomeAction | null;
  /** The conversion leg to record. */
  readonly conversion: CloseConversion;
  /** A non-conversion reason is REQUIRED before close (lead_not_converted). */
  readonly reasonRequired: boolean;
  /** The suggested default reason for the picker (primary recoverable decline). */
  readonly suggestedReason: string;
  /** The plan marks nothing -> the ONE inline question must be answered first. */
  readonly needsExplicitPick: boolean;
  /** The plain-language summary the doctor reads BEFORE clicking close. */
  readonly summary: string;
}

/**
 * Derive the whole close from the plan-item statuses (+ the inline-question
 * answer when the plan marks nothing). PURE and total. The matrix:
 *   - any accepted            -> treatment_accepted + converted
 *   - none accepted, >=1 declined -> treatment_declined + not-converted (reason required)
 *   - nothing marked          -> the inline question (advised / accepted / declined)
 */
export function deriveVisitClose(
  planItems: readonly ClinicalPlanItemView[],
  explicitPick: CloseExplicitPick,
): VisitCloseDerivation {
  const roll = rollupPlan(planItems);

  // R-A: any procedure accepted -> converted.
  if (roll.accepted > 0) {
    return {
      visitOutcome: 'treatment_accepted',
      conversion: 'converted',
      reasonRequired: false,
      suggestedReason: '',
      needsExplicitPick: false,
      summary: 'Treatment accepted - the visit will close as treatment accepted.',
    };
  }

  // R-B: no accepted, at least one declined -> not-converted + reason.
  if (roll.declined > 0) {
    const primary = primaryRecoverableDecline(planItems);
    return {
      visitOutcome: 'treatment_declined',
      conversion: 'not_converted',
      reasonRequired: true,
      suggestedReason: primary?.declineReason ?? '',
      needsExplicitPick: false,
      summary: 'Treatment declined - the visit will close as treatment declined.',
    };
  }

  // R-B2 (TREATMENT-ESTIMATE-ACCEPTANCE spine): no accepted, no declined,
  // at least one DEFERRED -> the patient made a real decision (postponed),
  // so the inline question is NOT re-asked; the visit closes on the
  // EXISTING advised outcome vocabulary with no conversion decision today
  // (the deferral itself stays recorded item-level on the plan).
  if (roll.deferred > 0) {
    return {
      visitOutcome: 'treatment_advised',
      conversion: 'none',
      reasonRequired: false,
      suggestedReason: '',
      needsExplicitPick: false,
      summary:
        'Treatment deferred - the visit will close with no conversion decision today (the deferral stays on the plan).',
    };
  }

  // R-C: nothing marked (only advised / pending / empty) -> the inline question.
  if (explicitPick === null) {
    return {
      visitOutcome: null,
      conversion: 'none',
      reasonRequired: false,
      suggestedReason: '',
      needsExplicitPick: true,
      // GRAIN NAMED IN THE SENTENCE (founder finding, #780 review). This
      // derivation reads THIS VISIT's plan items only. A patient part-way
      // through a course legitimately has accepted lines from an EARLIER
      // visit rendered on the same screen by the course card, so an
      // unqualified "no procedure was marked accepted or declined" reads as a
      // flat contradiction of the "Accepted - planned" chips above it. It was
      // never wrong -- it was answering a different question -- and the fix
      // is to say WHICH question, not to change what is counted.
      summary:
        'No procedure was accepted or declined IN THIS VISIT. How did this visit conclude?',
    };
  }
  if (explicitPick === 'accepted') {
    return {
      visitOutcome: 'treatment_accepted',
      conversion: 'converted',
      reasonRequired: false,
      suggestedReason: '',
      needsExplicitPick: false,
      summary: 'Recorded as accepted - the visit will close as treatment accepted.',
    };
  }
  if (explicitPick === 'declined') {
    return {
      visitOutcome: 'treatment_declined',
      conversion: 'not_converted',
      reasonRequired: true,
      suggestedReason: '',
      needsExplicitPick: false,
      summary: 'Recorded as declined - the visit will close as treatment declined.',
    };
  }
  // 'advised': a doctor recommendation with no patient decision today.
  return {
    visitOutcome: 'treatment_advised',
    conversion: 'none',
    reasonRequired: false,
    suggestedReason: '',
    needsExplicitPick: false,
    summary: 'Recorded as advised - the visit will close (no conversion decision today).',
  };
}

// --- PRE-CLOSE CLARITY (CHECKOUT-HANDOFF-AND-DOCTOR-CONTEXT-V1) --------------

/** Sum of the ACCEPTED items' non-null estimates (whole INR); null when no
 *  accepted item carries a value OR the recorded estimates sum to zero --
 *  never a fabricated zero, and the SAME >0 rule the handoff derivation
 *  uses (adversarial finding F2: a zero total must read "no estimate
 *  recorded" on EVERY surface, never "Rs 0 suggested"). Pure. */
export function acceptedEstimateTotal(
  planItems: readonly ClinicalPlanItemView[],
): number | null {
  let total: number | null = null;
  for (const i of planItems) {
    if (i.status === 'accepted' && typeof i.estimatedValue === 'number') {
      total = (total ?? 0) + i.estimatedValue;
    }
  }
  return total !== null && total > 0 ? total : null;
}

export interface CloseHeadline {
  /** The derived CONCLUSION, stated as a headline (PRE-CLOSE CLARITY LAW):
   *  "This visit will close as: CONVERTED" / "... NOT CONVERTED" /
   *  "... ADVISED ONLY" -- or the ONE inline question when nothing is
   *  derivable yet. */
  readonly headline: string;
  /** The CONSEQUENCE, stated before the confirm: what the front desk sees /
   *  collects, adapted to the R-1 grant (ungranted closes state the honest
   *  lock-and-handoff). Null only when nothing useful can be said. */
  readonly consequence: string | null;
}

/**
 * The pre-close headline + consequence per matrix row. PURE; the #246
 * derivation matrix stays the single source (this function only PHRASES its
 * result -- it never re-derives or re-asks). `canFulfil` adapts the
 * consequence to the R-1 grant; the accepted-estimate sum feeds the
 * collection line (HANDOFF LAW continuity: the same suggestion the front
 * desk will see on the day board).
 */
export function closeHeadline(
  derivation: VisitCloseDerivation,
  _planItems: readonly ClinicalPlanItemView[],
  canFulfil: boolean,
): CloseHeadline {
  if (derivation.needsExplicitPick) {
    return {
      headline: 'How did this visit conclude?',
      // Same grain, same reason as the R-C summary above: accepted lines
      // carried from an earlier visit in the course are NOT a decision
      // recorded today, and the banner has to say so or it reads as a
      // contradiction of the plan lines beside it.
      consequence: 'No procedure was accepted or declined in this visit.',
    };
  }
  if (derivation.visitOutcome === 'treatment_accepted') {
    return {
      // V-1 REQ-COMMERCIAL-VOCABULARY-UNIFICATION: the visit-outcome/close
      // fact is "Treatment accepted" -- the reserved "Converted" is the
      // payment-derived commercial state (#267) and the doctor closes BEFORE
      // any payment, so it never reads "Converted" here.
      headline: 'This visit will close as: TREATMENT ACCEPTED',
      // Accepted clinical estimates never establish an amount due today.
      consequence: 'The clinical record locks now. Reception checks the saved bill balance before collection.',
    };
  }
  if (derivation.visitOutcome === 'treatment_declined') {
    return {
      // V-1: the declined visit-outcome reads "Treatment declined" -- the
      // reserved "Converted" token (and its negation) belongs to the
      // payment-derived commercial plane, never the visit-outcome plane.
      headline: 'This visit will close as: TREATMENT DECLINED',
      consequence: canFulfil
        ? 'A reason is required - price / timing / no response queue a follow-up.'
        : 'A reason is required. The clinical record locks now. Reception checks the saved bill balance.',
    };
  }
  if (derivation.visitOutcome === 'treatment_advised') {
    return {
      headline: 'This visit will close as: ADVISED ONLY',
      consequence: canFulfil
        ? 'No payment handoff yet - the front desk sees Follow-up needed.'
        : 'No payment handoff yet - the clinical record locks now. Reception checks the saved bill balance.',
    };
  }
  // Unreachable by construction (every answered branch carries an outcome);
  // fall back to the derivation's own summary, never a blank.
  return { headline: derivation.summary, consequence: null };
}

/** Whether the close button may fire: the question is answered, and any
 *  required reason is present. Pure -- the strip disables the button from
 *  this, and closeVisit re-checks it (defense in depth). */
export function canFireClose(
  derivation: VisitCloseDerivation,
  reason: string,
): boolean {
  if (derivation.needsExplicitPick) return false;
  if (derivation.reasonRequired && reason.trim() === '') return false;
  return true;
}

// --- C7: CLOSE-COMPLETENESS + CONSENT ADVISORY LINES (WARN-ONLY) --------------
// DOCTOR-CONSULT-TRUTH-AND-GATE-CORRECTIVE-V1 (Tower walk 2026-08-14): a
// visit locked with 7 of 9 clinical fields "Not recorded" and consent still
// OWED, and the close confirm warned about neither. These two pure lines
// state those facts on the confirm panel BEFORE the lock. GATE FENCE:
// WARN-ONLY -- the close act itself stays exactly as ruled (one act,
// derive-not-ask); neither line ever blocks, and no blocking behavior may be
// added here without a founder ruling.

/**
 * The completeness summary line over the record's visible label/value rows
 * (the same rows the finalized card will render). null when every row
 * carries a value -- a complete record warns about nothing.
 */
export function closeCompletenessLine(
  rows: ReadonlyArray<{
    readonly label: string;
    readonly value: string | null;
  }>,
): string | null {
  if (rows.length === 0) return null;
  const missing = rows.filter(
    (r) => r.value === null || r.value.trim() === '',
  ).length;
  if (missing === 0) return null;
  return `${missing} of ${rows.length} clinical fields are Not recorded on this visit.`;
}

/**
 * The consent-owed line from the page's ONE consents read. null when the
 * wire is unavailable / consent is not required / nothing is owed.
 */
export function closeConsentOwedLine(owedCount: number | null): string | null {
  if (owedCount === null || owedCount <= 0) return null;
  return `Consent is still owed on ${owedCount} accepted item${
    owedCount === 1 ? '' : 's'
  }.`;
}

// --- The ONE-CLOSE orchestrator (client-side; composes EXISTING writes) ------

/** The injected domain ops closeVisit composes. Each is an EXISTING audited
 *  write (the component wires them to the api client fns). */
export interface CloseVisitDeps {
  /** POST /appointments/:id/fulfilled (R-1 checkout gate). */
  readonly fulfil: (appointmentId: number) => Promise<unknown>;
  /** recordOutcome -> POST /appointments/:id/treatment-{advised,accepted,declined}. */
  readonly recordVisitOutcome: (
    action: OutcomeAction,
    appointmentId: number,
    declineReason: string,
  ) => Promise<WorkbenchFeedback>;
  /** POST /appointments/:id/converted. */
  readonly markConverted: (appointmentId: number) => Promise<unknown>;
  /** POST /appointments/:id/not-converted { reason }. */
  readonly markNotConverted: (
    appointmentId: number,
    reason: string,
  ) => Promise<unknown>;
  /** PUT /case-sheets/:id { status: 'finalized' } -- the lock. */
  readonly finalize: (caseSheetId: number) => Promise<unknown>;
  /** Map any rejection to a PHI-safe line (the component owns the mapper). */
  readonly errorText: (err: unknown) => string;
  /** The stable machine code off a rejection (case_sheet_finalized idempotency). */
  readonly errorCode: (err: unknown) => string | null;
}

export interface CloseVisitInput {
  readonly appointmentId: number;
  /** The flushed case-sheet id (the component ensures the sheet exists first). */
  readonly caseSheetId: number;
  /** R-1: may the caller fulfil the visit (config-granted doctor checkout)? */
  readonly canFulfil: boolean;
  /** Server status (idempotency: skip fulfil when already 'fulfilled'). */
  readonly currentStatus: string;
  /** Recorded outcome tokens (idempotency: never re-record an event). */
  readonly recordedOutcomeEvents: readonly string[];
  readonly derivation: VisitCloseDerivation;
  /** The picked non-conversion reason (required for the not-converted leg). */
  readonly notConvertedReason: string;
}

export interface CloseVisitResult {
  readonly feedback: WorkbenchFeedback;
  /** True when the visit is finalized/locked after this run. */
  readonly closed: boolean;
}

/** The honest R-1 handoff copy (ungranted close: clinical facts locked; the
 *  front desk completes + the conversion records at checkout). */
export const CLOSE_HANDOFF_LINES: readonly string[] = [
  'The clinical record is complete and locked.',
  'Front desk completes the visit; the conversion is recorded at checkout.',
];

/**
 * Close the visit in ONE act. GRANTED (canFulfil): fulfil -> record the derived
 * visit outcome -> record the derived conversion -> finalize (lock). UNGRANTED
 * (R-1): finalize (lock) the clinical record + return the honest handoff -- the
 * plan-item statuses persist as the derivation source for the downstream
 * record. Every step is IDEMPOTENT and STOPS honestly on a partial failure
 * (the notes are already saved; a retry completes the rest). Never throws --
 * every rejection becomes feedback.
 */
/** THE TREATMENT OUTCOME, one sentence each, and the ONLY thing the close
 *  banner says. The VISIT STATUS belongs to the closed-visit panel; stating it
 *  here too is the duplication the 2026-09-11 ruling folded. */
export const CLOSE_OUTCOME_ACCEPTED_LINE = 'Treatment accepted.';
export const CLOSE_OUTCOME_DECLINED_LINE = 'Treatment declined.';
/** Neither accepted nor declined was derived: the honest third state, so the
 *  banner is never empty and never implies an outcome nobody recorded. */
export const CLOSE_OUTCOME_NONE_LINE =
  'No treatment outcome was recorded for this visit.';

export async function closeVisit(
  deps: CloseVisitDeps,
  input: CloseVisitInput,
): Promise<CloseVisitResult> {
  const { appointmentId, caseSheetId, derivation } = input;

  // Pre-checks (before any write): the inline question is answered, and a
  // required reason is present.
  if (derivation.needsExplicitPick) {
    return {
      closed: false,
      feedback: {
        kind: 'error',
        lines: ['Answer how the visit concluded before closing.'],
      },
    };
  }
  if (derivation.reasonRequired && input.notConvertedReason.trim() === '') {
    return {
      closed: false,
      feedback: {
        kind: 'error',
        lines: ['Pick a non-conversion reason first (it is required).'],
      },
    };
  }

  // UNGRANTED (R-1): record the clinical facts (finalize lock) + honest handoff.
  // No fulfil / outcome / conversion -- those need a fulfilled visit + the grant.
  if (!input.canFulfil) {
    try {
      await deps.finalize(caseSheetId);
    } catch (err) {
      if (deps.errorCode(err) !== 'case_sheet_finalized') {
        return {
          closed: false,
          feedback: { kind: 'error', lines: [deps.errorText(err)] },
        };
      }
    }
    return {
      closed: true,
      feedback: { kind: 'success', lines: [...CLOSE_HANDOFF_LINES] },
    };
  }

  // GRANTED: the doctor's ONE close fulfils + records + locks.
  const lines: string[] = [];

  // 1) Fulfil (skip when already fulfilled).
  if (input.currentStatus !== 'fulfilled') {
    try {
      await deps.fulfil(appointmentId);
      /* NO LINE FOR THIS STEP. "Visit completed." said the VISIT STATUS, and
         the closed-visit panel this close renders says it already ("Visit
         closed", then what the lock means). Three statements of one fact was
         the finding; the fulfil step narrating itself was the first of
         them. */
    } catch (err) {
      return {
        closed: false,
        feedback: {
          kind: 'error',
          lines: [
            `The visit could not be completed: ${deps.errorText(err)}`,
            'Your notes are saved. Try closing again.',
          ],
        },
      };
    }
  }

  // 2) Record the derived visit outcome (skip when already recorded).
  if (
    derivation.visitOutcome !== null &&
    !input.recordedOutcomeEvents.includes(derivation.visitOutcome)
  ) {
    let out: WorkbenchFeedback;
    try {
      out = await deps.recordVisitOutcome(
        derivation.visitOutcome,
        appointmentId,
        derivation.visitOutcome === 'treatment_declined'
          ? input.notConvertedReason
          : '',
      );
    } catch {
      // A committed outcome can have an unreadable receipt. Let the existing
      // page refresh saved truth before enabling retry; never repeat it here.
      return {closed:false,feedback:{kind:'error',lines:[
        'The visit outcome could not be verified. Your notes are saved.',
        'Check the reloaded visit before trying to finish again.',
      ]}};
    }
    if (out.kind === 'error') {
      return {
        closed: false,
        feedback: {
          kind: 'error',
          lines: [...out.lines, 'Your notes are saved. Try closing again.'],
        },
      };
    }
  }

  // 3) Record the derived conversion (skip when already recorded).
  if (
    derivation.conversion === 'converted' &&
    !input.recordedOutcomeEvents.includes('lead_converted')
  ) {
    try {
      await deps.markConverted(appointmentId);
    } catch (err) {
      return {
        closed: false,
        feedback: {
          kind: 'error',
          lines: [
            `The conversion could not be recorded: ${deps.errorText(err)}`,
            'Your notes are saved. Try closing again.',
          ],
        },
      };
    }
  } else if (
    derivation.conversion === 'not_converted' &&
    !input.recordedOutcomeEvents.includes('lead_not_converted')
  ) {
    try {
      await deps.markNotConverted(appointmentId, input.notConvertedReason);
    } catch (err) {
      return {
        closed: false,
        feedback: {
          kind: 'error',
          lines: [
            `The non-conversion could not be recorded: ${deps.errorText(err)}`,
            'Your notes are saved. Try closing again.',
          ],
        },
      };
    }
  }

  // 4) Finalize (lock) the case sheet (idempotent: an already-locked sheet is
  //    a success on this axis, not an error).
  try {
    await deps.finalize(caseSheetId);
  } catch (err) {
    if (deps.errorCode(err) !== 'case_sheet_finalized') {
      return {
        closed: false,
        feedback: {
          kind: 'error',
          lines: [
            `The visit was recorded but could not be locked: ${deps.errorText(err)}`,
            'Try closing again to finish.',
          ],
        },
      };
    }
  }

  /* TWO DISTINCT FACTS, EACH SAID ONCE (founder ruling 2026-09-11).
     After a close the screen used to state the visit's status three times --
     "Visit completed." here, "Visit closed - treatment accepted." here, and
     the closed panel's own "Visit closed" heading. The VISIT STATUS is the
     panel's to say, because the panel persists and this banner does not; what
     only this banner knows is the TREATMENT OUTCOME it just recorded, so that
     is all it says.

     V-1 still holds: the outcome is the clinical fact, never the reserved
     commercial "Converted" -- payment is recorded separately by the front
     desk, and the doctor's close is pre-payment by construction. */
  lines.push(
    derivation.conversion === 'converted'
      ? CLOSE_OUTCOME_ACCEPTED_LINE
      : derivation.conversion === 'not_converted'
        ? CLOSE_OUTCOME_DECLINED_LINE
        : CLOSE_OUTCOME_NONE_LINE,
  );
  return { closed: true, feedback: { kind: 'success', lines } };
}
